import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Separator } from '../ui/separator';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Trash2, UserX } from 'lucide-react';
import { toast } from 'sonner';

export const AccountCard = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleLogout = async () => { await logout(); navigate('/'); };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return;
    setDeleting(true);
    try {
      await api.delete('/user/account');
      navigate('/account-deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
      setDeleting(false);
    }
  };

  return (
    <Card className="glass border-white/20 dark:border-white/10">
      <CardHeader>
        <CardTitle className="font-['Manrope']">Account</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <Button variant="destructive" onClick={handleLogout} data-testid="logout-btn">Log Out</Button>
        <Separator />
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-destructive">
            <UserX className="w-5 h-5" />
            <Label className="text-destructive font-semibold">Delete Account</Label>
          </div>
          <p className="text-sm text-muted-foreground">
            Permanently delete your account and all associated data. This action cannot be undone.
          </p>
          {!showDeleteConfirm ? (
            <Button variant="outline" className="border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground" onClick={() => setShowDeleteConfirm(true)} data-testid="delete-account-btn">
              Delete My Account
            </Button>
          ) : (
            <div className="space-y-3 p-4 rounded-lg border border-destructive/30 bg-destructive/5">
              <p className="text-sm font-medium text-destructive">
                Type <span className="font-mono font-bold">DELETE</span> to confirm:
              </p>
              <Input value={deleteConfirmText} onChange={(e) => setDeleteConfirmText(e.target.value)} placeholder="Type DELETE" data-testid="delete-confirm-input" />
              <div className="flex gap-2">
                <Button variant="destructive" onClick={handleDeleteAccount} disabled={deleteConfirmText !== 'DELETE' || deleting} data-testid="confirm-delete-btn">
                  {deleting ? 'Deleting...' : 'Permanently Delete'}
                </Button>
                <Button variant="outline" onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(''); }} data-testid="cancel-delete-btn">
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
